import { Divider } from 'antd';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { ChannelContext, IGroup } from "../../../context/channel";
import colors from '../../../theme/colors';
import AutoComplete from '../autocomplete/AutoComplete';
import DropdownButton from '../dropdown/Dropdown';
import ButtonClose from './ButtonClose';
import ButtonSchedule, { ScheduleIcon } from './ButtonSchedule';
import { heightMobile } from './constants';
import Sidebar from './Sidebar';
import TopmenuButton from './TopmenuButton';

import { useTranslation } from "react-i18next";
import { ITalksProps } from '../../../interfaces/university/talk';
import { DEFAULT_PRIMARY_COLOR } from '../../../pages/university/Home';


interface ITrilhaConteudoHeaderProps {
  searchString?: string;
  setSearchString?: Dispatch<SetStateAction<string>>;
  handleSearch?: (() => void);
}

interface ISideBarTitle {
  colorHover: string;
}

const StyledTopmenuButton = styled(TopmenuButton)`
  display: block;

  ${(props) => props.theme.mediaQuerie.minWidth.md} {
    display: none;
  }
`;

const StyledTopmenuMobileLayoutSearch = styled.div`
  position: absolute;
  top: ${heightMobile};
  left: 0;
  width: 100vw;
  z-index: 999;
  background-color: ${(props) => props.theme.colors.white};
  border: 1px solid ${(props) => props.theme.colors.line};
  transition: height 0.25s, padding 0.5s;
  ${(props: { searchOpened: boolean }) => css`
  height: ${props.searchOpened ? '80px' : '0px'};
  padding: ${props.searchOpened ? '16px 0' : 0};
  `}
  ${(props) => props.theme.mediaQuerie.minWidth.md} {
    display: none;
  }

  .input-layout {
    display: flex;
    margin: 0 16px;
  }

  .input-layout-opened {
    margin: 0 16px;
  }

  .icon-search-container {
    display: flex;
    align-items: center;
  }
`;

const InputSearchContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${(props) => props.theme.colors.background};
  border: none;
  border-radius: 50px;
  width: 370px;
  height: 50px;
  margin-right: 32px;
  position: relative;
  z-index: 2;
  
  display: ${(props: { mobileOnly: boolean }) => (props.mobileOnly ? 'block' : 'none')};
  transition: all 0.5s;

  ${(props) => props.theme.mediaQuerie.minWidth.md} {
    display: flex;
    width: 370px;
  }
`;

const Search = styled.input`
  background-color: ${(props) => props.theme.colors.background};
  border: none;
  border-radius: 50px;
  width: 80%;
  height: 100%;
  margin-left: 10px;

  ::placeholder {
    color: ${(props) => props.theme.colors.placeholder};
  }

  :focus {
    outline: none;
}
`;

const IconSearch = () => (
  <svg width="19" height="24" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M8.32129 15.5C12.0492 15.5 15.0713 12.4779 15.0713 8.75C15.0713 5.02208 12.0492 2 8.32129 2C4.59337 2 1.57129 5.02208 1.57129 8.75C1.57129 12.4779 4.59337 15.5 8.32129 15.5Z"
      stroke={colors.secondary}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M16.5713 17L13.5713 14"
      stroke={colors.secondary}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const LayoutSidebar = styled.div`
  padding: 64px 16px 0;
  display: flex;
  flex-direction: column;

  .eventials-typography {
    margin-bottom: 16px;
  }
`;

const SideBarTitle = styled.a<ISideBarTitle>`
text-decoration: none;
color: ${(props) => props.theme.colors.secondary};
margin-bottom: 16px;
font-weight: 500;
cursor: pointer;
  &:hover {
    color: ${(props) => props.colorHover}
  }
`;

const TrilhaConteudoHeader: React.FC<ITrilhaConteudoHeaderProps> = ({ searchString, setSearchString }) => {
  const { data, groups, talks, channel } = React.useContext(ChannelContext)
  const [searchOpened, setSearchOpened] = useState<boolean>(false);
  const [sidebarOpened, setSidebarOpened] = useState<boolean>(false);
  const [suggestionsOpened, setSuggestionOpened] = useState<boolean>(false);
  const [suggestionsData, setSuggestionData] = useState<any>();

  const {t} = useTranslation();

  const history = useHistory();

  const handleChange = (e: any) => {
    if (setSearchString !== undefined) {
      setSearchString(e.target.value)
    }
    document.getElementById("styledSearch")?.focus()
  }

  useEffect(() => {
    if(searchString && searchString !== "" && searchString.length >= 2) {
      const arrayOfGroupTalks = groups?.map(g => {
        const talks = g.talks.filter(t => t.title.toLowerCase().includes(searchString.toLowerCase()));
        return talks;
      });

      let subgroupTalks: ITalksProps[] = [];

      groups?.map((g: IGroup) => (
        g.subgroups.map((s: any) => {
          const talks = s.talks.filter((t: ITalksProps) => t.title.toLowerCase().includes(searchString.toLowerCase()));
          return talks.map((t: ITalksProps) => {
            subgroupTalks.push(t);
            return t;
          });
        })
      ));

      // flattening array of arrays into a single array
      let groupTalks: any = arrayOfGroupTalks.reduce((a, b) => a.concat(b), []);

      //@ts-ignore
      let sTalks = [...talks?.filter(t => t.title.toLowerCase().includes(searchString.toLowerCase())), ...subgroupTalks, ...groupTalks];
      
      // ordering by alphabetic order
      sTalks = sTalks.sort((a: any,b: any) => (a.title > b.title) ? 1 : ((b.title > a.title) ? -1 : 0));

      // filtering and removing duplicated talks
      sTalks = sTalks.filter((v: any, i: number, a: any) => a.findIndex((t: any) =>(t.id === v.id)) === i);

      setSuggestionData({
        groups: groups?.filter(g => g.name.toLowerCase().includes(searchString.toLowerCase())),
        talks: sTalks,
      });

      setSuggestionOpened(true);
    } else {
      setSuggestionOpened(false);
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchString]);

  return (
    <>
      <InputSearchContainer mobileOnly={false}>
        <IconSearch />
        <Search
          placeholder={t('search.placeholder')!}
          onChange={(e) => handleChange(e)}
        />
      </InputSearchContainer>
      {suggestionsOpened &&
        <AutoComplete
          data={suggestionsData}
          channel={channel}
          setSuggestionData={setSuggestionData}
          setSuggestionOpened={setSuggestionOpened}
          setSearchString={setSearchString}
        />}
      {searchOpened ? (
        <ButtonClose
          borderLeft={false}
          className="desktop-hidden"
          onClick={() => setSearchOpened(false)}
        />
      ) : (
        <StyledTopmenuButton className="desktop-hidden" onClick={() => setSearchOpened(true)}>
          <IconSearch />
        </StyledTopmenuButton>
      )}
      {sidebarOpened ? (
        <ButtonClose
          borderLeft
          className="desktop-hidden"
          onClick={() => setSidebarOpened(false)}
        />
      ) : (
        <>
          <Divider type="vertical" style={{ height: "100%", paddingRight: "24px" }} />
          <ButtonSchedule
            className="desktop-hidden"
            borderLeft
            onClick={() => setSidebarOpened(true)}
          />
        </>
      )}

      <DropdownButton
        className="mobile-hidden"
        buttonProps={{
          typographyType: 'button3',
          enable: true,
          size: 'small',
          colorFont: 'black',
          customBackground:`${data.config.colors?.colorPrimary  ? data.config.colors.colorPrimary : DEFAULT_PRIMARY_COLOR}`,
          colorBackground: 'background',
          rightIcon: <ScheduleIcon />,
        }}
        title={t('filter')}
        menus={groups.map((group) => {
          return { title: group.name, slug: group.slug, key: group.slug, handleClick: (slug) => { history.push(`/${channel}/groups/${slug}`) }}
        })}
      />

      <StyledTopmenuMobileLayoutSearch searchOpened={searchOpened}>
        <InputSearchContainer mobileOnly={searchOpened} className={searchOpened ? "input-layout" : "input-layout-opened"} >
          <div className="icon-search-container">
            <IconSearch />
          </div>
          <Search
            placeholder={t('search.placeholder')!}
            onChange={(e) => handleChange(e)}
          />
        </InputSearchContainer>
      </StyledTopmenuMobileLayoutSearch>

      <Sidebar opened={sidebarOpened}>
        <div className="sidebar-header">
          <ButtonSchedule />
          <ButtonClose onClick={() => setSidebarOpened(false)} />
        </div>
        <LayoutSidebar>
          {groups.map((group, index) => {
            return (
              <SideBarTitle
                key={index}
                colorHover={data.config.colors?.colorPrimary}
                onClick={(e) => {e.preventDefault(); history.push(`/${channel}/groups/${group.slug}`); }}
                href="#"
              >
                {group.name}
              </SideBarTitle>
            )
          })}
        </LayoutSidebar>
      </Sidebar>
    </>
  );
};

export default TrilhaConteudoHeader;
